import { ComponentPropsWithRef } from "react"
import { cva, VariantProps } from "class-variance-authority"
import { cn } from "cn"

import { useDialogAutoFocus } from "./dialog-presentation"

export const INPUT_VARIANT_SIZES = {
  xs: "text-sm leading-4 px-2 py-1 h-(--input-xs-size)",
  sm: "text-sm leading-4 px-3 py-1.5 h-(--input-sm-size)",
  md: "text-base leading-5 px-3 py-2 h-(--input-md-size)",
  lg: "text-md leading-5 px-4 py-3 h-(--input-lg-size)",
  xl: "text-md leading-5 px-6 py-4 h-(--input-xl-size)",
  "2xl": "text-lg leading-6 px-7 py-5 h-(--input-2xl-size)",
}

const textInputVariants = cva(
  "peer flex w-full truncate rounded-full border border-input bg-background text-foreground transition-colors placeholder:text-muted not-disabled:hover:border-gray-8 focus:border-current focus:outline-hidden focus:placeholder:text-gray-8 data-invalid:border-destructive",
  {
    variants: {
      size: INPUT_VARIANT_SIZES,
      disabled: {
        true: "cursor-not-allowed opacity-50",
      },
      muted: {
        true: "border-transparent bg-foreground/5 focus:bg-background",
      },
      readOnly: {
        true: "bg-foreground/5",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

export interface TextInputProps
  extends
    Omit<ComponentPropsWithRef<"input">, "size">,
    VariantProps<typeof textInputVariants> {
  disabled?: boolean
  readOnly?: boolean
}

function TextInput({
  type = "text",
  value,
  size,
  disabled,
  className,
  muted,
  readOnly,
  autoFocus,
  ref,
  ...props
}: TextInputProps) {
  const autoFocusProps = useDialogAutoFocus(autoFocus, ref)

  return (
    <input
      type={type}
      disabled={disabled}
      value={value}
      {...autoFocusProps}
      className={cn(
        textInputVariants({
          size,
          disabled,
          readOnly,
          muted,
        }),
        className
      )}
      readOnly={readOnly}
      {...props}
    />
  )
}
TextInput.displayName = "TextInput"

export { TextInput, textInputVariants }
